import _ from 'lodash';
import React, {FunctionComponent} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Grid, Chip, makeStyles, ThemeProvider} from '@material-ui/core';
import {createTheme} from '@material-ui/core/styles';
import Checkbox from '@treez-ui/core/forms/Checkbox';
import {ProductData} from '@treez/commons/sharedTypings/product';
import {updateMergeProduct} from '~/redux/actions/MergeActions';
import {getFieldSelector} from '~/redux/selectors/mergeSelectors';
import {TreezColor, treezColorMap} from '~/styles/constants';

interface Props {
    field: ProductData;
    values: string[];
}

const {GREY_MEDIUM, OFF_WHITE, GREEN_HIGHLIGHT} = TreezColor;

export const MergeMultiValueProductGridCell: FunctionComponent<Props> = (props: Props) => {
    const {field, values} = props;

    const dispatch = useDispatch();

    const fieldSelector = getFieldSelector(field);

    const selectedValues = useSelector(fieldSelector, _.isEqual) as string[];

    const handleValueSelect = (value: string) => {
        if (_.includes(selectedValues, value)) {
            const newValueSet = _.filter(selectedValues, (sv) => {
                return sv !== value;
            }) as string[];

            dispatch(updateMergeProduct(field, newValueSet));
        } else {
            dispatch(updateMergeProduct(field, [...selectedValues, value]));
        }
    };

    const handleSelectAll = (value: boolean) => {
        if (value === true) {
            const newValueSet = [..._.uniq([...selectedValues, ...values])];

            dispatch(updateMergeProduct(field, newValueSet));
        } else {
            const newValueSet = _.filter(selectedValues, (sv) => {
                return !_.includes(values, sv);
            });

            dispatch(updateMergeProduct(field, newValueSet));
        }
    };

    const allValuesSelected =
        _.size(values) > 0 &&
        _.every(values, (value) => {
            return _.includes(selectedValues, value);
        });

    const theme = createTheme({
        palette: {
            primary: {
                main: treezColorMap[OFF_WHITE],
            },
            secondary: {
                main: treezColorMap[GREEN_HIGHLIGHT],
            },
        },
    });

    const useStyles = makeStyles({
        multiValueField: {
            borderLeft: '1px solid grey',
            borderTop: '1px solid grey',
            height: '200px',
            maxWidth: '100%',
            width: '100%',
        },
        singleValue: {
            height: '20px',
        },
        valuesContainer: {
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
        },
        singleValueContainer: {
            borderBottom: `1px solid ${treezColorMap[GREY_MEDIUM]}`,
            margin: '4px',
        },
    });

    const {multiValueField, valuesContainer, singleValueContainer} = useStyles();

    return (
        <ThemeProvider theme={theme}>
            <Grid item className={multiValueField}>
                <Grid item container justifyContent="flex-end" sm={12}>
                    <Checkbox
                        checked={allValuesSelected}
                        label=""
                        onChange={(e, checked) => handleSelectAll(checked)}
                    />
                </Grid>
                <Grid className={valuesContainer}>
                    {_.map(values, (value, idx) => {
                        return (
                            <Chip
                                clickable
                                className={singleValueContainer}
                                color={_.includes(selectedValues, value) ? 'secondary' : 'primary'}
                                key={`${value}-${idx}`}
                                label={value}
                                onClick={() => handleValueSelect(value)}
                            />
                        );
                    })}
                </Grid>
            </Grid>
        </ThemeProvider>
    );
};

export default React.memo(MergeMultiValueProductGridCell);
